/**
 * ColorSwatchToggleGroup
 *
 * Multi-select row of toggleable color swatches (checkbox semantics). Shared
 * by the note filters and the share filter field groups so the swatch UX and
 * a11y stay identical everywhere.
 *
 * @module components/common/ColorSwatchToggleGroup
 */
import { memo } from 'react';
import { ColorSwatch, Group } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

import { ICON_SIZE_XS, INPUT_LABEL_MARGIN_BOTTOM } from '@/constants/ui';
import { NOTE_COLOR_SWATCH_SIZE } from '@/constants/notes';

import styles from './ColorSwatchToggleGroup.module.css';

interface ColorSwatchToggleGroupProps {
  colors: readonly string[];
  selected: string[];
  onToggle: (color: string) => void;
  /** Offset below an `Input.Wrapper` label (mirrors Mantine input spacing). */
  withLabelOffset?: boolean;
}

export const ColorSwatchToggleGroup = memo(function ColorSwatchToggleGroup({
  colors,
  selected,
  onToggle,
  withLabelOffset = false,
}: ColorSwatchToggleGroupProps) {
  return (
    <Group
      gap="xs"
      className={styles.colorRow}
      mt={withLabelOffset ? INPUT_LABEL_MARGIN_BOTTOM : undefined}
    >
      {colors.map((color) => {
        const isSelected = selected.includes(color);
        const swatchClassName = isSelected
          ? `${styles.colorSwatch} ${styles.colorSwatchSelected}`
          : styles.colorSwatch;

        return (
          <ColorSwatch
            key={color}
            color={color}
            size={NOTE_COLOR_SWATCH_SIZE}
            onClick={() => onToggle(color)}
            className={swatchClassName}
            role="checkbox"
            aria-checked={isSelected}
            aria-label={color}
          >
            {isSelected && (
              <IconCheck size={ICON_SIZE_XS} className={styles.checkIcon} />
            )}
          </ColorSwatch>
        );
      })}
    </Group>
  );
});
